import React, {
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";

import PageLayout from "../../layouts/PageLayout";

import {
    Row,
    Col,
    Form,
    Button,
    Container,
} from "react-bootstrap";

import Select from "react-select";

import { TranslatorContext } from "../../context/Translator";

import {
    Link,
    useNavigate,
    useLocation
} from "react-router-dom";

import axios from "axios";

export default function IssuerProductEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Accept item either directly or nested under lister_products[0]
    const rawItem = location.state?.item;
    const passedItem = rawItem?.lister_products?.[0] ? rawItem.lister_products[0] : rawItem;

    // --------------------------------------------------
    // Loading / Submit
    // --------------------------------------------------

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // --------------------------------------------------
    // API Data
    // --------------------------------------------------

    
    const activeTab = location.state?.activeTab || "new"; 

    const [categories, setCategories] = useState([]);
    const [childCategories, setChildCategories] = useState([]);
    const [listers, setListers] = useState([]);
    const [mplacers, setMplacers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [stores, setStores] = useState([]);
    const [selectedParentCategory, setSelectedParentCategory] = useState("");

    // --------------------------------------------------
    // Study Checkbox State
    // --------------------------------------------------

    const [isStudy, setIsStudy] = useState(false);

    // --------------------------------------------------
    // Form
    // --------------------------------------------------

    const [formData, setFormData] = useState({
        category_id: "",
        lister_id: "",
        mplacer_id: "",
        brand_id: "",
        store_id: "",
        product_condition: "Used",
        selling_style: "Fixed",
        title: "",
        short_buying_price: "",
        buying_price: "",
        sell_price_dollar: "",
        auction_min_price: "",
        shipping_charges_dollar: "",
    });

    // --------------------------------------------------
    // Image & Previews
    // --------------------------------------------------

    const [firstImg, setFirstImg] = useState(null);
    const [imgPreview, setImgPreview] = useState(null);

    // --------------------------------------------------
    // Errors & Camera
    // --------------------------------------------------

    const [errors, setErrors] = useState({});
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const videoRef = useRef(null);

    // ==================================================
    // POPULATE CATEGORIES (Parent & Sub Category Selection)
    // ==================================================

    const handleCategoryMapping = useCallback((categoriesList, selectedCategoryId) => {
        if (!selectedCategoryId || !categoriesList.length) return;

        const targetCatId = String(selectedCategoryId);

        for (const parent of categoriesList) {
            if (String(parent.id) === targetCatId) {
                setSelectedParentCategory(String(parent.id));
                setChildCategories(parent.child_categories || []);
                return;
            }

            if (Array.isArray(parent.child_categories)) {
                const matchedChild = parent.child_categories.find(
                    (child) => String(child.id) === targetCatId
                );

                if (matchedChild) {
                    setSelectedParentCategory(String(parent.id));
                    setChildCategories(parent.child_categories);
                    setFormData((prev) => ({
                        ...prev,
                        category_id: String(matchedChild.id),
                    }));
                    return;
                }
            }
        }
    }, []);

    // ==================================================
    // FETCH INITIAL DATA
    // ==================================================

    const fetchInitialData = useCallback(async () => {
        setLoading(true);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/products/initial-data`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                const fetchedCategories = data.product_categories || data.categories || [];

                setCategories(fetchedCategories);
                setListers(data.listers || []);
                setMplacers(data.mplacers || []);
                setBrands(data.brands || []);
                setStores(data.stores || []);

                // Pre-fill form values when item is available
                if (passedItem) {
                    const catId = passedItem.category?.id ? String(passedItem.category.id) : "";
                    const sellingPrice = passedItem.sell_price_dollar;

                    // Condition: If sell_price_dollar is null, 0, or empty -> Check Study Box automatically
                    const shouldBeStudy =
                        sellingPrice === null ||
                        sellingPrice === undefined ||
                        sellingPrice === "" ||
                        parseFloat(sellingPrice) === 0;

                    setIsStudy(shouldBeStudy);

                    setFormData({
                        title: passedItem.title || "",
                        category_id: catId,
                        lister_id: passedItem.lister?.id ? String(passedItem.lister.id) : rawItem?.lister?.id ? String(rawItem.lister.id) : "",
                        mplacer_id: passedItem.marketPlacer?.id ? String(passedItem.marketPlacer.id) : rawItem?.marketPlacer?.id ? String(rawItem.marketPlacer.id) : "",
                        brand_id: passedItem.brand?.id ? String(passedItem.brand.id) : "",
                        store_id: passedItem.store?.id ? String(passedItem.store.id) : rawItem?.store?.id ? String(rawItem.store.id) : "",
                        product_condition: passedItem.product_condition || "used",
                        selling_style: passedItem.selling_style || "Fixed",
                        short_buying_price: passedItem.short_buying_price ?? "",
                        buying_price: passedItem.buying_price ?? "",
                        sell_price_dollar: shouldBeStudy ? "" : sellingPrice ?? "",
                        auction_min_price: passedItem.auction_min_price ?? passedItem.pricing?.auction_min_price ?? "",
                        shipping_charges_dollar: passedItem.shipping_charges_dollar ?? "",
                    });

                    if (passedItem.first_img) {
                        setImgPreview(`${process.env.REACT_APP_IMG_URL}${passedItem.first_img}`);
                    }

                    if (catId) {
                        handleCategoryMapping(fetchedCategories, catId);
                    }
                }
            }
        } catch (error) {
            console.error("Error loading initial form data:", error);
        } finally {
            setLoading(false);
        }
    }, [passedItem, rawItem, handleCategoryMapping]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Cleanup camera stream
    useEffect(() => {
        return () => {
            if (cameraStream) {
                cameraStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [cameraStream]);

    // ==================================================
    // PARENT CATEGORY CHANGE
    // ==================================================

    const handleParentCategoryChange = (selectedOption) => {
        const parentId = selectedOption ? String(selectedOption.value) : "";
        setSelectedParentCategory(parentId);

        const parentCat = categories.find(
            (cat) => String(cat.id) === parentId
        );

        if (parentCat && Array.isArray(parentCat.child_categories)) {
            setChildCategories(parentCat.child_categories);
        } else {
            setChildCategories([]);
        }

        setFormData((prev) => ({
            ...prev,
            category_id: "",
        }));

        if (errors.category_id) {
            setErrors((prev) => ({
                ...prev,
                category_id: null,
            }));
        }
    };

    // Helper for React-Select change handling
    const handleSelectChange = (name, selectedOption) => {
        const value = selectedOption ? String(selectedOption.value) : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    // Standard input handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    // Study Checkbox Change Handler
    const handleStudyChange = (e) => {
        const checked = e.target.checked;
        setIsStudy(checked);

        if (checked) {
            setFormData((prev) => ({
                ...prev,
                sell_price_dollar: "",
            }));
            if (errors.sell_price_dollar) {
                setErrors((prev) => ({
                    ...prev,
                    sell_price_dollar: null,
                }));
            }
        }
    };

    // Camera Functions
    const openCamera = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert("Camera is not supported by this browser.");
                return;
            }

            stopCamera();

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });

            setCameraStream(stream);
            setCameraOpen(true);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch((err) => console.error("Video play error:", err));
                }
            }, 100);
        } catch (error) {
            console.error("Camera error:", error);
            alert("Unable to open camera.");
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraStream(null);
        setCameraOpen(false);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) {
            alert("Camera is not ready yet.");
            return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;

            const file = new File([blob], `product-${Date.now()}.jpg`, {
                type: "image/jpeg",
            });

            setFirstImg(file);
            setImgPreview(URL.createObjectURL(file));

            if (errors.first_img) {
                setErrors((prev) => ({
                    ...prev,
                    first_img: null,
                }));
            }
            stopCamera();
        }, "image/jpeg", 0.9);
    };

    const removeImage = () => {
        setFirstImg(null);
        setImgPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        const submitData = new FormData();

        Object.keys(formData).forEach((key) => {
            if (key === "auction_min_price" && formData.selling_style !== "Auction") {
                return;
            }
            if (key === "sell_price_dollar" && isStudy) {
                // Ignore / send empty for selling price when Study option is selected
                return;
            }
            if (formData[key] !== null && formData[key] !== "") {
                submitData.append(key, formData[key]);
            }
        });

        if (firstImg) {
            submitData.append("first_img", firstImg);
        }
        submitData.append("_method", "PATCH");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/issuer/products/${passedItem?.id}`,
                submitData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                navigate(`/issue-product-list?tab=${activeTab}`);
            }
        } catch (error) {
            if (error.response && error.response.status === 422) {
                setErrors(error.response.data.errors || {});
            } else {
                console.error("Error storing product:", error);
                alert("Something went wrong while saving the product.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner"></div>
                    <p>Loading Product Setup...</p>
                </div>
            </PageLayout>
        );
    }

    // Mapping dropdown options
    const storeOptions = stores.map((store) => ({ value: String(store.id), label: store.name }));
    const parentCatOptions = categories.map((cat) => ({ value: String(cat.id), label: cat.name }));
    const childCatOptions = childCategories.map((child) => ({ value: String(child.id), label: child.name }));
    const brandOptions = brands.map((brand) => ({ value: String(brand.id), label: brand.name }));
    const listerOptions = listers.map((lister) => ({ value: String(lister.id), label: `★ ${lister.nickname} 👤 ${lister.name}`, }));
    const mplacerOptions = mplacers.map((mplacer) => ({ value: String(mplacer.id), label: `★ ${mplacer.nickname} 👤 ${mplacer.name}`, }));

    return (
        <PageLayout>
            <Container fluid className="px-0">
                <Row>
                    <Col xl={12}>
                        <div className="mc-card">
                            <div className="mc-breadcrumb">
                                <h3 className="mc-breadcrumb-title">Edit Product</h3>

                                {passedItem?.rejection_reason && (
                                    <div className="alert alert-danger py-2 px-3 my-2 text-center" role="alert">
                                        <strong className="text-danger">{t("rejection reason")}: </strong>
                                        <span className="text-dark">{passedItem.rejection_reason}</span>
                                    </div>
                                )}

                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home") || "Home"}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/products" className="mc-breadcrumb-link">
                                            {t("products") || "Products"}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">Edit Product</li>
                                </ul>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col lg={8}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Product Details</h4>
                                <Row>
                                    {/* STORE DROPDOWN WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Store <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Store..."
                                                options={storeOptions}
                                                value={storeOptions.find((opt) => opt.value === String(formData.store_id)) || null}
                                                onChange={(opt) => handleSelectChange("store_id", opt)}
                                            />
                                            {errors.store_id && (
                                                <div className="text-danger small mt-1">{errors.store_id[0]}</div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Product Condition <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="product_condition"
                                                value={formData.product_condition}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.product_condition}
                                            >
                                                <option value="used">Used</option>
                                                <option value="new">New</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.product_condition && errors.product_condition[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* PARENT CATEGORY WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Parent Category</Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Parent Category..."
                                                options={parentCatOptions}
                                                value={parentCatOptions.find((opt) => opt.value === String(selectedParentCategory)) || null}
                                                onChange={handleParentCategoryChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* SUB CATEGORY WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Sub Category <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                isDisabled={!selectedParentCategory}
                                                placeholder="Select Sub Category..."
                                                options={childCatOptions}
                                                value={childCatOptions.find((opt) => opt.value === String(formData.category_id)) || null}
                                                onChange={(opt) => handleSelectChange("category_id", opt)}
                                            />
                                            {errors.category_id && (
                                                <div className="text-danger small mt-1">{errors.category_id[0]}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Title <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.title}
                                        placeholder="e.g. Rolex Submariner Date 126610LN"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.title && errors.title[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    {/* BRAND DROPDOWN WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Brand <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Brand..."
                                                options={brandOptions}
                                                value={brandOptions.find((opt) => opt.value === String(formData.brand_id)) || null}
                                                onChange={(opt) => handleSelectChange("brand_id", opt)}
                                            />
                                            {errors.brand_id && (
                                                <div className="text-danger small mt-1">{errors.brand_id[0]}</div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Selling Style <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="selling_style"
                                                value={formData.selling_style}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.selling_style}
                                            >
                                                <option value="Fixed">Fixed</option>
                                                <option value="Auction">Auction</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.selling_style && errors.selling_style[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* LISTER DROPDOWN WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Assign Lister</Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Lister (Optional)..."
                                                options={listerOptions}
                                                value={listerOptions.find((opt) => opt.value === String(formData.lister_id)) || null}
                                                onChange={(opt) => handleSelectChange("lister_id", opt)}
                                            />
                                            {errors.lister_id && (
                                                <div className="text-danger small mt-1">{errors.lister_id[0]}</div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* MARKETPLACE USER WITH SEARCH */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Assign Marketplace User</Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Marketplace User (Optional)..."
                                                options={mplacerOptions}
                                                value={mplacerOptions.find((opt) => opt.value === String(formData.mplacer_id)) || null}
                                                onChange={(opt) => handleSelectChange("mplacer_id", opt)}
                                            />
                                            {errors.mplacer_id && (
                                                <div className="text-danger small mt-1">{errors.mplacer_id[0]}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            {/* PRICING CONFIGURATION */}
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Pricing Configuration</h4>
                                <Row>
                                    {/* BUYING PRICE PKR */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Buying Price PKR <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="buying_price"
                                                value={formData.buying_price}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.buying_price}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.buying_price && errors.buying_price[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* SHORT BUYING PRICE */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Short Buying Price</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="short_buying_price"
                                                value={formData.short_buying_price}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.short_buying_price}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.short_buying_price && errors.short_buying_price[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* SHIPPING CHARGES ($) */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Shipping Charges ($) <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="shipping_charges_dollar"
                                                value={formData.shipping_charges_dollar}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.shipping_charges_dollar}
                                                placeholder="0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.shipping_charges_dollar && errors.shipping_charges_dollar[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* SELLING PRICE ($) WITH STUDY CHECKBOX */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Form.Label className="mb-0">
                                                    Selling Price ($) {!isStudy && <span className="text-danger">*</span>}
                                                </Form.Label>
                                                <Form.Check
                                                    type="checkbox"
                                                    id="study-checkbox"
                                                    label="Study"
                                                    checked={isStudy}
                                                    onChange={handleStudyChange}
                                                    className="small font-weight-bold"
                                                />
                                            </div>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="sell_price_dollar"
                                                value={formData.sell_price_dollar}
                                                onChange={handleInputChange}
                                                disabled={isStudy}
                                                isInvalid={!!errors.sell_price_dollar}
                                                placeholder={isStudy ? "Study Mode (Disabled)" : "0.00"}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.sell_price_dollar && errors.sell_price_dollar[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {formData.selling_style === "Auction" && (
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Auction Min Price ($)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    name="auction_min_price"
                                                    value={formData.auction_min_price}
                                                    onChange={handleInputChange}
                                                    isInvalid={!!errors.auction_min_price}
                                                    placeholder="0.00"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.auction_min_price && errors.auction_min_price[0]}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Col>

                        <Col lg={4}>
                            <div className="mc-card mb-4">
                                <h4 className="card-title mb-4">Main Product Image</h4>

                                <div className="text-center mb-3">
                                    <Button
                                        type="button"
                                        variant="dark"
                                        className="w-100"
                                        onClick={openCamera}
                                    >
                                        📷 Take Photo
                                    </Button>
                                </div>

                                {imgPreview && (
                                    <div className="text-center mt-3">
                                        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                            <img
                                                src={imgPreview}
                                                alt="Product Preview"
                                                className="img-fluid rounded border"
                                                style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                                            />
                                            <Button
                                                type="button"
                                                variant="danger"
                                                size="sm"
                                                style={{
                                                    position: "absolute",
                                                    top: "8px",
                                                    right: "8px",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    padding: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                                onClick={removeImage}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mc-card">
                                <Button
                                    type="submit"
                                    className="btn btn-primary w-100 mb-2"
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving Product..." : "Save Product"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-100" onClick={() => navigate(`/issue-product-list?tab=${activeTab}`)}  disabled={submitting}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Container>

            {cameraOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.92)",
                        zIndex: 99999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: "700px",
                            backgroundColor: "#000",
                            borderRadius: "12px",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: "15px",
                                color: "#fff",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <h5 style={{ margin: 0 }}>Take Product Photo</h5>
                            <Button type="button" variant="danger" size="sm" onClick={stopCamera}>
                                Close
                            </Button>
                        </div>

                        <div
                            style={{
                                width: "100%",
                                background: "#000",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: "100%",
                                    maxHeight: "70vh",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </div>

                        <div style={{ padding: "20px", display: "flex", gap: "10px" }}>
                            <Button type="button" variant="primary" className="w-100" onClick={capturePhoto}>
                                📸 Capture Photo
                            </Button>
                            <Button type="button" variant="secondary" className="w-100" onClick={stopCamera}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}